/**
 *
 * Wallets
 *
 */

import React, { memo, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Conditional, Text, Space, toast } from '@cashfree-intl/coherent';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import { MerchantContext } from 'contexts/MerchantContext';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import Loader from 'components/Loader';
import { getItemFromLocalStorage } from 'utils/common';
import {
  PGDB_SHOW_DISPUTES_WALLET,
  WALLET_RECHARGE_ACCOUNT_NAME,
} from 'utils/constants';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';

import makeSelectWallets from './selectors';
import reducer from './reducer';
import saga from './saga';
import WalletIntro from './components/WalletIntro';
import WalletCards from './components/WalletCards';
import {
  addMoneyToWalletAction,
  createWalletAction,
  disableLoaderAction,
  enableLoaderAction,
  getWalletBalanceAction,
  getWalletStatusAction,
  modifyWalletStatusAction,
  updateFeeWalletDetailsAction,
  updateWalletBalanceAction,
} from './actions';
import { DISPUTE_WALLET, FEE_WALLET, REFUND_WALLET } from './constants';
import { IWallets } from './types';
import FeeWallet from './components/FeeWallet';
import WalletsBanner from './components/WalletsBanner';
import { getWalletBankList } from 'services/pgConfig';

export function Wallets({
  getWalletStatus,
  getWalletBalance,
  addMoneyToWallet,
  modifyWalletStatus,
  enableLoader,
  disableLoader,
  createWallet,
  updateWalletBalance,
  updateFeeWalletStatus,
  wallets: {
    walletsData,
    walletBalance,
    isLoading,
    bankDetails,
    feeWallet,
    disputeWallet,
    disputeWalletBankDetails,
  },
}: IWallets) {
  useInjectReducer({ key: 'wallets', reducer });
  useInjectSaga({ key: 'wallets', saga, mode: '' });

  const { featureToggles = {} } = useContext(MerchantContext);

  const merchantId = getItemFromLocalStorage('merchantId');

  const [isRefundWalletActive, setIsRefundWalletActive] = useState(false);
  const [isFeeWalletActive, setIsFeeWalletActive] = useState(false);
  const [disputeWalletActive, setDisputeWalletActive] = useState(false);
  const [bankList, setBankList] = useState([]);

  const showDisputesWallet =
    PGDB_SHOW_DISPUTES_WALLET in featureToggles &&
    featureToggles[PGDB_SHOW_DISPUTES_WALLET];

  const getFeeWalletStatus = () => {
    enableLoader();
    getWalletStatus({
      walletType: FEE_WALLET,
      cb: () => {
        disableLoader();
        setIsFeeWalletActive(true);
      },
      errorCb: () => {
        setIsFeeWalletActive(false);
      },
    });
  };

  const getDisputeWalletStatus = () => {
    enableLoader();
    getWalletStatus({
      walletType: DISPUTE_WALLET,
      cb: () => {
        setDisputeWalletActive(true);
        getWalletBalance({
          walletType: DISPUTE_WALLET,
          cb: () => {
            disableLoader();
          },
        });
      },
      errorCb: () => {
        setDisputeWalletActive(false);
      },
    });
  };

  const getWalletInfo = () => {
    enableLoader();
    getWalletStatus({
      walletType: REFUND_WALLET,
      cb: () => {
        setIsRefundWalletActive(true);
        getWalletBalance({
          walletType: 'refundWallet',
          cb: () => {
            disableLoader();
          },
        });
      },
      errorCb: () => {
        setIsRefundWalletActive(false);
      },
    });
    getFeeWalletStatus();
    if (showDisputesWallet) {
      getDisputeWalletStatus();
    }
  };

  const getBankLists = async () => {
    try {
      const response = await getWalletBankList();
      setBankList(response);
    } catch (error) {
      toast.error(error);
      setBankList([]);
    }
  };

  useEffect(() => {
    getWalletInfo();
    getBankLists();
  }, []);

  const handleRefreshClick = walletType => (e: any) => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.REFRESH_WALLET_BALANCE);
    updateWalletBalance({});
    e.stopPropagation();
    enableLoader();
    getWalletBalance({ walletType, cb: () => disableLoader() });
  };

  const handleAddMoneyToWalletClick = (walletType = 'Refund Wallet') => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.ADD_MONEY);
    enableLoader();
    addMoneyToWallet({ walletType });
  };

  const handleToggleChange = (toggleStatus: string, walletType: string) => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.UPDATE_WALLET_STATUS);
    enableLoader();
    modifyWalletStatus({
      updatedBy: merchantId,
      name: walletType,
      status: toggleStatus,
    });
  };

  const handleCreateWallet = (walletType: string) => {
    if (walletType === 'Refund Wallet') {
      createWallet({ name: 'REFUND', createdBy: merchantId });
    } else
      createWallet({ createdBy: merchantId, type: 'cash', name: merchantId });
  };

  const { status, balance } = feeWallet;

  const handleFeeWalletRefreshClick = (e: any) => {
    e.stopPropagation();
    updateFeeWalletStatus({});
    getFeeWalletStatus();
  };

  const renderFeeWallet = () => (
    <>
      {/* <Conditional if={!isRefundWalletActive}>
        <Text variant="h20" className="mt-5 mb-2">
          Your Wallets
        </Text>
      </Conditional> */}
      <FeeWallet
        status={status}
        balance={balance}
        handleRefreshClick={handleFeeWalletRefreshClick}
        handleAddMoneyToWalletClick={handleAddMoneyToWalletClick}
        bankDetails={{
          ...bankDetails,
          accountName: WALLET_RECHARGE_ACCOUNT_NAME,
        }}
        getWalletInfo={getFeeWalletStatus}
      />
    </>
  );

  return (
    <div>
      <Loader active={isLoading} />
      <Conditional if={!isRefundWalletActive || !disputeWalletActive}>
        <>
          <WalletIntro
            bankDetails={{
              ...bankDetails,
              accountName: WALLET_RECHARGE_ACCOUNT_NAME,
            }}
            handleAddMoneyToWalletClick={handleCreateWallet}
            getWalletInfo={getWalletInfo}
            refundWalletActive={isRefundWalletActive}
            disputeWalletActive={disputeWalletActive}
            showDisputesWallet={!!showDisputesWallet}
            bankList={bankList}
          />
          <Conditional
            if={
              !isRefundWalletActive && !disputeWalletActive && isFeeWalletActive
            }
          >
            <div className="mt-4">{renderFeeWallet()}</div>
          </Conditional>
        </>
      </Conditional>
      <Conditional if={isRefundWalletActive || disputeWalletActive}>
        <Conditional if={isRefundWalletActive && disputeWalletActive}>
          <WalletsBanner />
        </Conditional>
        <Text variant="b14" color="bodyLight" className="my-4">
          You can view and manage the associated wallets in this screen.
        </Text>
        <Space gap={4} wrap="wrap">
          <WalletCards
            walletsData={{
              refundWallet: walletsData,
              disputeWallet,
            }}
            showDisputesWallet={!!showDisputesWallet}
            walletBalance={walletBalance}
            handleRefreshClick={handleRefreshClick}
            handleAddMoneyToWalletClick={handleAddMoneyToWalletClick}
            handleToggleChange={handleToggleChange}
            getWalletInfo={getWalletInfo}
            bankDetails={{
              refundWalletBankDetails: {
                ...bankDetails,
                accountName: WALLET_RECHARGE_ACCOUNT_NAME,
              },
              disputeWalletBankDetails: {
                ...disputeWalletBankDetails,
                accountName: WALLET_RECHARGE_ACCOUNT_NAME,
              },
            }}
            disputeWalletActive={disputeWalletActive}
            refundsWalletActive={isRefundWalletActive}
            bankList={bankList}
          />
          <Conditional if={isFeeWalletActive}>{renderFeeWallet()}</Conditional>
        </Space>
      </Conditional>
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  wallets: makeSelectWallets(),
});

export function mapDispatchToProps(dispatch: any) {
  return {
    getWalletStatus: (data: {
      walletType: string;
      cb: Function;
      errorCb: Function;
    }) => dispatch(getWalletStatusAction(data)),
    getWalletBalance: (data: { cb: () => void }) =>
      dispatch(getWalletBalanceAction(data)),
    addMoneyToWallet: (data: { walletType: string }) =>
      dispatch(addMoneyToWalletAction(data)),
    modifyWalletStatus: (data: {
      updatedBy: string;
      name: string;
      status: string;
    }) => dispatch(modifyWalletStatusAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    createWallet: (data: { createdBy: string; name: string }) =>
      dispatch(createWalletAction(data)),
    updateWalletBalance: (data: any) =>
      dispatch(updateWalletBalanceAction(data)),
    updateFeeWalletStatus: (data: any) =>
      dispatch(updateFeeWalletDetailsAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Wallets);
